/**
 * Companion chat-UI slice.
 *
 * Owns the Telegram-style sliding chat UI: view level, selected chat agent,
 * list navigation, and per-agent unread counts + last-message previews.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState } from "../useAdsWorkspaceStore.types";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type CompanionChatUiActions = Pick<
  AdsWorkspaceState,
  | "setCompanionViewLevel"
  | "selectChatAgent"
  | "goBackToChatList"
  | "incrementUnread"
  | "clearUnread"
  | "updateLastMessagePreview"
>;

export function createCompanionChatUiSlice(
  set: SetState,
  _get: GetState,
): CompanionChatUiActions {
  return {
    setCompanionViewLevel: (level) => set({ companionViewLevel: level }),

    selectChatAgent: (agentId) =>
      set((state) => ({
        selectedChatAgent: agentId,
        companionViewLevel: "chat",
        // Keep activeSubAgent in sync for backward compat with SubAgentChatView
        activeSubAgent: agentId,
        // Clear unread for the agent we're opening
        unreadCounts: {
          ...state.unreadCounts,
          [agentId ?? "marcus"]: 0,
        },
        // Reveal the agent if it's a sub-agent
        revealedSubAgents:
          agentId && !state.revealedSubAgents.includes(agentId)
            ? [...state.revealedSubAgents, agentId]
            : state.revealedSubAgents,
        // Opening a sub-agent counts as "seen" — clear its transient busy flag
        // so the inline activity notifier in Marcus disappears.
        subAgentBusy: agentId ? { ...state.subAgentBusy, [agentId]: false } : state.subAgentBusy,
      })),

    goBackToChatList: () =>
      set({
        companionViewLevel: "list",
        selectedChatAgent: null,
        activeSubAgent: null,
      }),

    incrementUnread: (agentId) =>
      set((state) => ({
        unreadCounts: {
          ...state.unreadCounts,
          [agentId]: (state.unreadCounts[agentId] ?? 0) + 1,
        },
      })),

    clearUnread: (agentId) =>
      set((state) => ({
        unreadCounts: {
          ...state.unreadCounts,
          [agentId]: 0,
        },
      })),

    updateLastMessagePreview: (agentId, preview) =>
      set((state) => ({
        lastMessagePreviews: {
          ...state.lastMessagePreviews,
          [agentId]: preview,
        },
      })),
  };
}
